import { Divider, Skeleton } from '@heroui/react';
import { ArrowSeparate } from 'iconoir-react';
import { useSelector } from 'react-redux';

import { ActionHandlers } from './DeployedPipelinesTable/cellRenderers';
import { DeploymentActionsMenu } from './DeploymentActionsMenu';
import { DeploymentDataItem } from '../hooks/useDeploymentsData';
import { getMessageStatus, StatusKey } from '../utils/deploymentUtils';

import { isDigitalWorkerPipeline } from '~/common/helpers/digitalWorker';
import i18n from '~/common/helpers/i18n';
import { TriggerCategory } from '~/common/utils/triggerCategoryIcons';
import Card from '~/components/Card';
import {
  DeploymentIssueIndicator,
  getDeploymentIssueSummary
} from '~/components/DeploymentIssueIndicator';
import { DeploymentTime } from '~/components/DeploymentTime';
import { DigitalWorkerTag } from '~/components/DigitalWorkerTag';
import { EnvironmentIndicator } from '~/components/EnvironmentIndicator';
import { TriggerIcon } from '~/components/TriggerIcon';

type DeploymentCardProps = {
  deployment: DeploymentDataItem['deployment'];
  menuActionHandlers: ActionHandlers;
};

export function DeploymentCard({
  deployment,
  menuActionHandlers
}: DeploymentCardProps) {
  const { environment } = useSelector(
    (state: {
      application: { environment: string; realm: { realm: string } };
    }) => state.application
  );

  const deploymentStatus = deployment.status.text.toLowerCase();

  const showPromote = environment !== 'prod';
  const isDisabled = [
    'starting',
    'degraded',
    'deleting',
    'service_error'
  ].includes(deploymentStatus);

  const getDeploymentSize = (consumers?: number) => {
    if (!consumers) return '';
    if (consumers === 10) return i18n.t('scenes.runtime.labels.size_small');
    if (consumers === 20) return i18n.t('scenes.runtime.labels.size_medium');
    if (consumers === 40) return i18n.t('scenes.runtime.labels.size_large');
    return '';
  };

  const deploymentSize = getDeploymentSize(
    deployment.activeConfiguration?.consumers
  );

  const { text } = getMessageStatus({
    deployStatus: deploymentStatus as StatusKey
  });

  const newEnvironment =
    menuActionHandlers.environment === 'test' ? 'prod' : 'test';

  const promoteValue = i18n.t(`action.move_to_${newEnvironment}`);
  const testIdPrefix = 'run-deployed-pipelines-grid-view-deployment';

  // Surfaced on the card so a problem the user can act on — an edited Global or Account waiting
  // for a redeploy, out of memory — is visible without opening the details sidesheet.
  const issueSummary = getDeploymentIssueSummary(deployment);

  return (
    <button
      data-testid={`${testIdPrefix}-details`}
      aria-label='Open deployment details'
      onClick={() => menuActionHandlers.onClick(deployment)}
      type='button'
      className='text-left'
    >
      <Card>
        <Card.Header
          icon={
            <TriggerIcon
              triggerCategory={
                deployment.pipeline.triggerCategory as TriggerCategory
              }
              iconSrc={deployment.pipeline.trigger?.iconURL}
              pipeline={{
                // BACKEND-API-TRIGGER: drop extra fields; pass triggerSpec only.
                triggerSpec: deployment.pipeline.triggerSpec as
                  { name: string; type: string } | undefined,
                hasApiTrigger: deployment.pipeline.hasApiTrigger,
                isUsingApiTrigger: deployment.pipeline.isUsingApiTrigger,
                flowSpec: deployment.pipeline.flowSpec
              }}
            />
          }
          menuComponent={
            <DeploymentActionsMenu
              showPromote={showPromote}
              isDisabled={isDisabled}
              environment={environment}
              testIdPrefix={testIdPrefix}
              onPromote={() =>
                setTimeout(
                  () =>
                    menuActionHandlers.openDetailPromote({
                      text: promoteValue,
                      newEnvironment,
                      ...deployment
                    }),
                  200
                )
              }
              onRedeploy={() =>
                setTimeout(
                  () =>
                    menuActionHandlers.redeploy({
                      ...deployment.pipeline,
                      isPipelineEngine:
                        deployment.deploymentStatus?.pipelineEngineBeta ===
                        'true'
                    }),
                  200
                )
              }
              onRollback={() =>
                menuActionHandlers.openModalRollbackDeploy(deployment)
              }
              onDeleteDeploy={() =>
                menuActionHandlers.onDeleteDeploy(deployment)
              }
              onGoToPipeline={() =>
                menuActionHandlers.goToPipeline(deployment.pipeline.id)
              }
              onViewExecutions={() =>
                menuActionHandlers.onViewExecutions(deployment.pipeline.name)
              }
              onOpenVersionDiff={() =>
                menuActionHandlers.onOpenVersionDiff(
                  deployment.pipeline.name,
                  `${deployment.pipeline.versionMajor}.${deployment.pipeline.versionMinor}`
                )
              }
              pipelineVersionMajor={deployment.pipeline.versionMajor}
              pipelineVersionMinor={deployment.pipeline.versionMinor}
            />
          }
        />
        <Card.Content
          title={deployment.pipeline.name}
          titleEndContent={
            isDigitalWorkerPipeline(deployment.pipeline.description) ? (
              <DigitalWorkerTag />
            ) : null
          }
          description={deployment.project}
        >
          <div className='mt-4 flex gap-6 py-2.5'>
            <div className='flex flex-col'>
              <p className='text-label-lg text-on-surface'>
                {deployment.activeConfiguration?.actualConsumers}/
                {deployment.activeConfiguration?.consumers}
              </p>
              <p className='text-label-md text-on-surface-variant'>
                {deploymentSize}
              </p>
            </div>
            <Divider orientation='vertical' className='h-9 bg-outline' />
            <div className='flex flex-col'>
              <p className='text-label-lg text-on-surface'>
                {deployment.availableReplicas?.split('/')[0] || 0}/
                {deployment.activeConfiguration?.replicaCount}
              </p>
              <p className='text-label-md text-on-surface-variant'>
                {i18n.t('scenes.runtime.labels.replicas')}
              </p>
            </div>
            {Number.isInteger(
              deployment.activeConfiguration?.minReplicaCount
            ) ? (
              <>
                <Divider orientation='vertical' className='h-9 bg-outline' />
                <div className='flex flex-col'>
                  <div className='flex items-center gap-1'>
                    <p className='text-label-lg text-on-surface'>
                      {deployment.activeConfiguration.minReplicaCount}
                    </p>
                    <ArrowSeparate className='icon-smallest' />
                    <p className='text-label-lg text-on-surface'>
                      {deployment.activeConfiguration.replicaCount}
                    </p>
                  </div>
                  <p className='text-label-md text-on-surface-variant'>
                    {i18n.t('scenes.runtime.labels.autoscaling')}
                  </p>
                </div>
              </>
            ) : null}
          </div>
        </Card.Content>
        {/* 20px above the tags and 16px below them, as the design has it — `Card.Footer` adds a
            top margin the other cards want. */}
        <Card.Footer className='mt-0 flex p-4 pt-5'>
          {/* Wraps as a last resort: the card is clipped at its rounded corners, so a footer that
              does not fit would cut the problem indicator in half instead of pushing a line. */}
          <div className='flex w-full flex-wrap items-center gap-x-2.5 gap-y-2'>
            <Card.Version
              versionMajor={deployment.pipeline.versionMajor}
              versionMinor={deployment.pipeline.versionMinor}
            />
            <DeploymentTime
              timestamp={deployment.startTime}
              deployedBy={deployment.metadata?.userName}
              engineVersion={deployment.deploymentStatus.pipelineEngineVersion}
            />
            {/* Order follows the design: environment, status, then the problem indicator last.
                6px apart, and never squeezed — the tags are the part that has to stay readable. */}
            <div className='ml-auto flex shrink-0 items-center gap-1.5'>
              {deployment.activeConfiguration?.environment?.name && (
                // The design draws this as a tag like the others, padding included — it just has
                // no background of its own.
                <EnvironmentIndicator
                  className='px-1'
                  environment={deployment.activeConfiguration.environment.name}
                />
              )}
              <p className='flex h-5 items-center whitespace-nowrap rounded-md bg-surface-dim px-1 text-label-md text-on-surface'>
                {text.toLowerCase()}
              </p>
              <DeploymentIssueIndicator
                summary={issueSummary}
                data-testid={`${testIdPrefix}-issues`}
              />
            </div>
          </div>
        </Card.Footer>
      </Card>
    </button>
  );
}

export function DeploymentCardSkeleton() {
  return (
    <Card isLoaded={false} data-testid='deployment-card-skeleton'>
      <div className='flex w-full items-center justify-between gap-1 p-4'>
        <Skeleton className='size-10 rounded-md' />
        <Skeleton className='size-8 rounded-md' />
      </div>
      <Card.Content title='title' description='description'>
        <div className='mt-4 flex gap-6 py-2.5'>
          <Skeleton className='h-9 w-9 rounded-md' />
          <Divider orientation='vertical' className='h-9 bg-outline' />
          <Skeleton className='h-9 w-9 rounded-md' />
        </div>
      </Card.Content>

      {/* Same footer box as the loaded card, so nothing shifts when the data arrives. */}
      <Card.Footer className='mt-0 flex p-4 pt-5'>
        <div className='flex w-full items-center gap-2.5'>
          <Card.Version versionMajor={0} versionMinor={0} />

          <Skeleton className='h-5 w-24 rounded-md' />

          <Skeleton className='ml-auto h-5 w-14 rounded-md' />
        </div>
      </Card.Footer>
    </Card>
  );
}
